// OWNER: Agent C. Ledger across everything, filterable by account and type.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Text } from "@cloudflare/kumo/components/text";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Button } from "@cloudflare/kumo/components/button";
import { Table } from "@cloudflare/kumo/components/table";
import { Select } from "@cloudflare/kumo/components/select";
import { formatMoney, formatUnits } from "@shared/format";
import type { TransactionType } from "@shared/types";
import { useAllHoldingsByAccount, useTransactions } from "../../lib/hooks";
import { PageHeader } from "../../components/PageHeader";
import { TableSkeleton } from "../../components/Skeletons";

const TYPE_ITEMS: Record<"ALL" | TransactionType, string> = {
  ALL: "All types",
  BUY: "Buy",
  SELL: "Sell",
  CONTRIBUTION: "Contribution",
  WITHDRAWAL: "Withdrawal",
  DIVIDEND: "Dividend",
  INTEREST: "Interest",
  VEST: "Vest",
  EXERCISE: "Exercise",
  FEE: "Fee",
  ADJUSTMENT: "Adjustment",
};

export default function TransactionsPage() {
  const navigate = useNavigate();
  const holdingsQuery = useAllHoldingsByAccount();
  const transactions = useTransactions({ limit: 200 });

  const [holdingId, setHoldingId] = useState<string>("ALL");
  const [type, setType] = useState<"ALL" | TransactionType>("ALL");

  const holdingNameById = useMemo(() => {
    const map = new Map<number, string>();
    if (holdingsQuery.status === "success") {
      for (const list of holdingsQuery.data.holdingsByAccount.values()) {
        for (const h of list) map.set(h.id, h.name);
      }
    }
    return map;
  }, [holdingsQuery]);

  const holdingItems = useMemo(() => {
    const items: Record<string, string> = { ALL: "All holdings" };
    for (const [id, name] of holdingNameById) items[String(id)] = name;
    return items;
  }, [holdingNameById]);

  const filtered = useMemo(() => {
    if (transactions.status !== "success") return [];
    return transactions.data.items.filter((t) => {
      if (holdingId !== "ALL" && t.holdingId !== Number(holdingId)) return false;
      if (type !== "ALL" && t.type !== type) return false;
      return true;
    });
  }, [transactions, holdingId, type]);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader title="Transactions" description="Adding a transaction happens from Investments — pick the holding first." />

      <div className="flex flex-wrap gap-3">
        <Select
          aria-label="Filter by holding"
          className="w-full sm:w-64"
          value={holdingId}
          onValueChange={(v) => setHoldingId((v as string) ?? "ALL")}
          items={holdingItems}
        />
        <Select
          aria-label="Filter by type"
          className="w-full sm:w-48"
          value={type}
          onValueChange={(v) => setType((v as "ALL" | TransactionType) ?? "ALL")}
          items={TYPE_ITEMS}
        />
      </div>

      {transactions.status === "loading" && <TableSkeleton rows={8} />}
      {transactions.status === "error" && <Text variant="error">{transactions.error.message}</Text>}
      {transactions.status === "success" && filtered.length === 0 && (
        <Empty
          title="No transactions"
          description="Nothing matches these filters yet — try widening them, or head to Investments to add your first transaction."
          contents={
            <Button variant="primary" onClick={() => navigate("/investments")}>
              Go to Investments
            </Button>
          }
        />
      )}
      {transactions.status === "success" && filtered.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>Date</Table.Head>
                <Table.Head>Holding</Table.Head>
                <Table.Head>Type</Table.Head>
                <Table.Head>Amount</Table.Head>
                <Table.Head>Units</Table.Head>
                <Table.Head>Note</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filtered.map((t) => (
                <Table.Row key={t.id} className="cursor-pointer" onClick={() => navigate(`/investments/${t.holdingId}`)}>
                  <Table.Cell>{t.txnDate}</Table.Cell>
                  <Table.Cell>{holdingNameById.get(t.holdingId) ?? `#${t.holdingId}`}</Table.Cell>
                  <Table.Cell>{TYPE_ITEMS[t.type]}</Table.Cell>
                  <Table.Cell>{formatMoney(t.amount, t.currency)}</Table.Cell>
                  <Table.Cell>{t.units != null ? formatUnits(t.units) : "—"}</Table.Cell>
                  <Table.Cell>{t.note ?? "—"}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}
